import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import {
  Container, Row, Col, Badge, Button,
  Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label,
} from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import {
  fetchPendingApprovals,
  approveLicenseRequest,
  rejectLicenseRequest,
} from '../../actions/cspLicensesActions'
import CSPLicenseDevPanel from './CSPLicenseDevPanel'
import BasketLineRow from './BasketLineRow'
import { formatDateTime } from '../../Shared/formatDateTime'
import { canUseLicenceFeature } from './featureAccess'

const biggestDelta = (request) => {
  const lines = request.lineItems || []
  if (lines.length === 0) return 0
  return lines.reduce((acc, li) => {
    const d = li.requestedQuantity - li.previousQuantity
    return Math.abs(d) > Math.abs(acc) ? d : acc
  }, 0)
}

// Approval modal: shows the staged basket alongside what live quantities
// looked like at submit-time (stored on the line item's PreviousQuantity
// after the live-read step routed the request to Pending). The
// approve-time pipeline re-reads DD before any UPDATE call, so even a
// stale view here can't push a wrong number through — but flagging
// drift early lets approvers cancel out of a clearly-stale basket
// instead of triggering the inevitable STALE_QUANTITY failure.
const ApprovalModal = ({ request, isOpen, toggle }) => {
  const dispatch = useDispatch()
  const lifecycleRequestId = useSelector((s) => s.cspLicenses.lifecycleRequestId)
  const isBusy = lifecycleRequestId === (request && request.id)
  const [showReject, setShowReject] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  if (!request) return null

  const submitApprove = async () => {
    const ok = window.confirm(
      `Approve ${request.requesterDisplayName || request.requesterUpn}'s licence change for ${request.companyName}? `
      + 'This will send the change to Dicker Data immediately.')
    if (!ok) return
    const result = await dispatch(approveLicenseRequest(request.id))
    if (result && result.ok) toggle()
  }

  const submitReject = async () => {
    const result = await dispatch(rejectLicenseRequest(request.id, rejectReason))
    if (result && result.ok) {
      setShowReject(false)
      setRejectReason('')
      toggle()
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Approve licence change &middot; {request.companyName}
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md={6}>
            <div className="small text-muted">Requester</div>
            <div>{request.requesterDisplayName || request.requesterUpn}</div>
            <div className="small text-muted">{request.requesterUpn}</div>
          </Col>
          <Col md={6}>
            <div className="small text-muted">CW ticket</div>
            <div>#{request.cwTicketId}</div>
            <div className="small text-muted">Submitted {formatDateTime(request.createdAt)}</div>
          </Col>
        </Row>

        <div className="small text-muted mb-1">Requested changes</div>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>SKU</th>
              <th className="text-right">Before</th>
              <th className="text-right">After</th>
            </tr>
          </thead>
          <tbody>
            {(request.lineItems || []).map((li) => (
              <BasketLineRow key={li.id}
                skuDisplayName={li.skuDisplayName}
                stockCode={li.stockCode}
                stockDescription={li.stockDescription}
                previousQuantity={li.previousQuantity}
                requestedQuantity={li.requestedQuantity}
              />
            ))}
          </tbody>
        </table>

        <div className="alert alert-info py-2 small mb-0">
          When you approve, we'll re-check the current seat count at
          Dicker Data. If it's drifted since the requester submitted, the
          change will be blocked and the requester gets a clear
          explanation.
        </div>

        {showReject && (
          <div className="mt-3 p-3 border rounded">
            <FormGroup>
              <Label className="small">Reason (optional)</Label>
              <Input
                type="textarea"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Different SKU mix needed — please re-stage."
              />
            </FormGroup>
            <Button color="danger" size="sm" disabled={isBusy} onClick={submitReject}>
              Confirm reject
            </Button>{' '}
            <Button color="link" size="sm" onClick={() => setShowReject(false)}>
              Cancel
            </Button>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isBusy}>
          Close
        </Button>
        <Button color="outline-danger" onClick={() => setShowReject(true)} disabled={isBusy || showReject}>
          Reject…
        </Button>
        <Button color="success" onClick={submitApprove} disabled={isBusy || showReject}>
          {isBusy ? 'Submitting…' : 'Approve'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const CSPLicenseApprovalsInner = () => {
  const dispatch = useDispatch()
  const pendingApprovals = useSelector((s) => s.cspLicenses.pendingApprovals)
  const loading = useSelector((s) => s.cspLicenses.pendingApprovalsLoading)
  // The selected request is a snapshot taken when the approver clicks
  // Review, NOT a derived value from pendingApprovals. The action
  // layer returns { ok: false } on operational failure (DD non-0000,
  // sign-flip, hard cap) so the modal can stay open and show the
  // error toast - but it also refreshes /requests/pending, which
  // drops the now-Failed request from the queue. If the modal derived
  // its content from the live list it would silently disappear at
  // the exact moment the approver needs to see what went wrong. The
  // snapshot stays put until the approver clicks Close (toggle) or a
  // successful action explicitly closes it.
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    dispatch(fetchPendingApprovals())
  }, [dispatch])

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row>
          <Col>
            <Link to="/CSPLicenses" className="small">&larr; Back to 365 Licenses</Link>
            <PageHeading>Approvals</PageHeading>
            <small className="text-muted">
              Licence change requests from other team members that need a
              review before they reach Dicker Data.
            </small>
          </Col>
        </Row>

        {loading ? (
          <LoadingSpinner />
        ) : !pendingApprovals || pendingApprovals.length === 0 ? (
          <div className="text-muted mt-3">No pending approvals right now.</div>
        ) : (
          <table className="table table-hover mt-3">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Requester</th>
                <th>Customer</th>
                <th className="text-right">Lines</th>
                <th className="text-right">Biggest line change</th>
                <th>CW</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pendingApprovals.map((r) => {
                const delta = biggestDelta(r)
                return (
                  <tr key={r.id}>
                    <td><small>{formatDateTime(r.createdAt)}</small></td>
                    <td>{r.requesterDisplayName || r.requesterUpn}</td>
                    <td>{r.companyName}</td>
                    <td className="text-right">{(r.lineItems || []).length}</td>
                    <td className="text-right">
                      <Badge color={delta > 0 ? 'warning' : 'info'}>
                        {delta > 0 ? `+${delta}` : delta}
                      </Badge>
                    </td>
                    <td><code className="small">#{r.cwTicketId}</code></td>
                    <td>
                      <Button color="primary" size="sm" onClick={() => setSelected(r)}>
                        Review
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        <ApprovalModal
          request={selected}
          isOpen={!!selected}
          toggle={() => setSelected(null)}
        />
      </Container>
      <CSPLicenseDevPanel />
    </PageContainer>
  )
}

const CSPLicenseApprovals = () => (
  <AzureAD provider={authProvider} reduxStore={store}>
    {({ accountInfo }) => {
      // Outer gate uses real AAD roles - the dev override is for
      // testing role-specific behaviour AFTER you're past the front
      // door. An engineer with no peer-approval role in real AAD has
      // no menu link here anyway; this is the direct-URL backstop.
      const realRoles = accountInfo?.account?.idToken?.roles || []
      const allowed = canUseLicenceFeature(realRoles)
      if (!allowed) {
        return (
          <PageContainer>
            <Container fluid className="mt-3">
              <p>You do not have permission to view this page.</p>
            </Container>
          </PageContainer>
        )
      }
      return <CSPLicenseApprovalsInner />
    }}
  </AzureAD>
)

export default CSPLicenseApprovals
